import { useMemo, useRef, useState } from 'react';
import { SHOW_LESS, SHOW_MORE } from 'common/constants/pageFilters';
import { Currency } from 'common/types/format';
import {
  PrimaryNavigationMenu,
  PrimaryNavigationMenuItem,
  PrimaryNavigationMenuSearch,
} from 'components/ui';

interface CurrencyMenuOption extends Record<string, unknown> {
  code: string;
  symbol: string;
}

interface CurrencyMenuProps {
  anchorEl: HTMLButtonElement | null;
  currencies: CurrencyMenuOption[];
  'data-testid'?: string;
  isCurrencyLoading: boolean;
  onClose: () => void;
  onCurrencyChange: (currency: Currency) => void;
  searchableCurrencies?: CurrencyMenuOption[];
  showMore: () => void;
  showLess: () => void;
  usedCurrencies: string[];
}

const CurrencyMenu = ({
  anchorEl,
  currencies,
  'data-testid': dataTestId,
  isCurrencyLoading,
  onClose,
  onCurrencyChange,
  searchableCurrencies,
  showMore,
  showLess,
  usedCurrencies,
}: CurrencyMenuProps) => {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchValue, setSearchValue] = useState('');
  const isSearchable = Boolean(searchableCurrencies);
  const normalizedSearchValue = searchValue.trim().toLowerCase();
  const visibleCurrencies = useMemo(() => {
    if (!normalizedSearchValue) {
      return currencies;
    }

    return (searchableCurrencies ?? currencies).filter(({ code, symbol }) => (
      code.toLowerCase().includes(normalizedSearchValue)
      || symbol.toLowerCase().includes(normalizedSearchValue)
    ));
  }, [currencies, normalizedSearchValue, searchableCurrencies]);

  const handleClose = () => {
    setSearchValue('');
    onClose();
  };

  const handleCurrencyChange = (currency: CurrencyMenuOption) => {
    setSearchValue('');
    onCurrencyChange(currency);
  };

  return (
    <PrimaryNavigationMenu
      anchorEl={anchorEl}
      data-testid={dataTestId}
      disableAutoFocusItem={isSearchable}
      open={Boolean(anchorEl)}
      onClose={handleClose}
      TransitionProps={isSearchable ? { onEntered: () => searchInputRef.current?.focus() } : undefined}>
      {isSearchable && (
        <PrimaryNavigationMenuSearch
          clearAriaLabel="Clear currency search"
          disabled={isCurrencyLoading}
          fullWidth
          inputRef={searchInputRef}
          placeholder="Find a Currency"
          value={searchValue}
          onChange={event => setSearchValue(event.target.value)}
          onClear={() => setSearchValue('')}
        />
      )}
      {visibleCurrencies.map(currency => {
        const isLastUsed = currency.code === usedCurrencies[usedCurrencies.length - 1];
        return (
          <PrimaryNavigationMenuItem
            key={currency.code}
            disabled={isCurrencyLoading}
            sx={isLastUsed && !normalizedSearchValue ? { borderBottom: '1px solid #EFEFEF' } : undefined}
            onClick={() => handleCurrencyChange(currency)}>
            {`${currency.code} (${currency.symbol})`}
          </PrimaryNavigationMenuItem>
        );
      })}
      {normalizedSearchValue && visibleCurrencies.length === 0 && (
        <PrimaryNavigationMenuItem disabled tone="muted">
          No currencies found
        </PrimaryNavigationMenuItem>
      )}
      {!normalizedSearchValue && (
        <PrimaryNavigationMenuItem
          disabled={isCurrencyLoading}
          sx={{ fontSize: '0.875rem', color: 'text.secondary' }}
          onClick={currencies.length === usedCurrencies.length ? showMore : showLess}>
          {currencies.length === usedCurrencies.length ? SHOW_MORE : SHOW_LESS}
        </PrimaryNavigationMenuItem>
      )}
    </PrimaryNavigationMenu>
  );
};

export default CurrencyMenu;
